import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../components/common/Footer';
import './SupportPage.css';

const ISSUE_TYPES = [
  'Booking help',
  'Driver issue',
  'Payment / refund',
  'Cancellation',
  'Driver registration',
  'Other',
];

const FAQS = [
  {
    q: 'How do I book a ride on ZilaCab?',
    a: 'Fill the booking form on our homepage, click "View Estimated Fare", select your vehicle, and confirm. We\'ll call you within 15 minutes to assign a driver. No app needed.',
  },
  {
    q: 'Why is ZilaCab cheaper for one-way trips?',
    a: 'Most operators charge you a return fare even when you only travel one way. ZilaCab matches your trip with a return passenger when possible, so you pay only for your side of the journey.',
  },
  {
    q: 'What is the TaxiPool surcharge?',
    a: 'TaxiPool gives you a discounted fare when a return passenger is found. If no match exists for your route on that day, a small surcharge applies — but you always see the exact amount before you confirm.',
  },
  {
    q: 'How do I cancel a booking?',
    a: 'Reply to the WhatsApp confirmation message or call the helpline at least 2 hours before pickup. Cancellations before driver dispatch are free.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'UPI (GPay, PhonePe, Paytm), cash on arrival, or bank transfer. No advance payment required for most rides.',
  },
  {
    q: 'How do I get a refund?',
    a: 'If you cancel before driver dispatch, refunds are instant via the original payment method. For other cases, our support team processes refunds within 24 hours.',
  },
  {
    q: 'Can ZilaCab travel to other states?',
    a: 'Yes — we run outstation trips to Jharkhand, UP, West Bengal, and Nepal border towns. State permit charges are shown upfront in your fare estimate.',
  },
  {
    q: 'How do I register as a driver?',
    a: 'Visit the Drive with us page and fill the registration form. Our team verifies documents within 24 hours and you can start receiving bookings within a week.',
  },
];

const HOURS = [
  { day: 'Monday – Friday', time: '6:00 AM – 11:00 PM' },
  { day: 'Saturday', time: '6:00 AM – 11:00 PM' },
  { day: 'Sunday', time: '7:00 AM – 10:00 PM' },
  { day: 'WhatsApp', time: '24 / 7' },
  { day: 'Emergency (active ride)', time: 'Always available' },
];

const SupportPage = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bookingRef, setBookingRef] = useState('');
  const [issueType, setIssueType] = useState(ISSUE_TYPES[0]);
  const [message, setMessage] = useState('');
  const [openFaq, setOpenFaq] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone || !message) {
      toast.error('Please fill name, phone and message');
      return;
    }
    toast.success("Message sent! We'll get back within 2 hours.");
  };

  return (
    <div className="support-page">
      <section className="support-hero">
        <div className="support-hero-left">
          <div className="section-eyebrow on-dark" style={{ color: 'var(--orange-light, #FF7B3A)' }}>We're here to help</div>
          <h1>Support. <em>Real humans.</em> Always.</h1>
          <p>No bots, no ticket queues. Call us, WhatsApp us, or email us — and a real ZilaCab team member will respond within 15 minutes during operating hours.</p>
        </div>
        <div className="contact-cards">
          <a href="tel:1800XXXXXXX" className="contact-card">
            <div className="cc-ic or">📞</div>
            <div className="cc-info">
              <h4>Toll-free helpline</h4>
              <p>1800-XXX-XXXX · Free from any network · 6 AM – 11 PM</p>
            </div>
            <div className="cc-arrow">→</div>
          </a>
          <a href="https://wa.me/919800000000" target="_blank" rel="noopener noreferrer" className="contact-card">
            <div className="cc-ic gr">💬</div>
            <div className="cc-info">
              <h4>WhatsApp support</h4>
              <p>+91 98000 00000 · Fastest response · 24/7</p>
            </div>
            <div className="cc-arrow">→</div>
          </a>
          <a href="mailto:hello@zilacab.com" className="contact-card">
            <div className="cc-ic bl">✉️</div>
            <div className="cc-info">
              <h4>Email support</h4>
              <p>hello@zilacab.com · Response within 4 hours</p>
            </div>
            <div className="cc-arrow">→</div>
          </a>
        </div>
      </section>

      <section className="support-main">
        <form className="support-form-card" onSubmit={handleSubmit}>
          <h3>Send us a message</h3>
          <p>Fill this form and we'll get back to you within 2 hours.</p>
          <div className="support-form-grid">
            <div className="fg">
              <label>Your name</label>
              <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="fg">
              <label>Phone number</label>
              <input type="tel" placeholder="+91 98000 00000" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </div>
            <div className="fg span2">
              <label>Booking reference (optional)</label>
              <input type="text" placeholder="ZC-XXXXX" value={bookingRef} onChange={(e) => setBookingRef(e.target.value.toUpperCase())} />
            </div>
            <div className="fg span2">
              <label>Issue type</label>
              <select value={issueType} onChange={(e) => setIssueType(e.target.value)}>
                {ISSUE_TYPES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="fg span2">
              <label>Your message</label>
              <textarea placeholder="Describe your issue or question..." value={message} onChange={(e) => setMessage(e.target.value)} required />
            </div>
            <div className="span2">
              <button type="submit" className="btn-or" style={{ width: '100%', padding: 14, background: 'var(--orange, #E8590A)', color: '#fff', border: 'none', borderRadius: 10, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 16px rgba(232,89,10,0.3)' }}>
                Send message →
              </button>
            </div>
          </div>
        </form>

        <div className="faq-section">
          <h3>Frequently asked questions</h3>
          <p>Quick answers to the most common questions.</p>
          <div className="faq-list">
            {FAQS.map((f, idx) => {
              const open = openFaq === idx;
              return (
                <div key={f.q} className={`faq-item${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="faq-q"
                    onClick={() => setOpenFaq(open ? -1 : idx)}
                    aria-expanded={open}
                  >
                    <span>{f.q}</span>
                    <span className="arrow">›</span>
                  </button>
                  {open && <div className="faq-a">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hours-section">
        <div className="hours-inner">
          <div>
            <div className="section-eyebrow">Operating hours</div>
            <h2 className="section-title">When we're available</h2>
            <p className="section-sub" style={{ marginBottom: 0 }}>Our support team is available during these hours. For urgent booking issues — WhatsApp us any time, 24/7.</p>
            <div className="hours-table">
              {HOURS.map((h) => (
                <div key={h.day} className="ht-row">
                  <span className="ht-day">{h.day}</span>
                  <span className="ht-time on">{h.time}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="emergency-card">
            <h3>Active ride emergency?</h3>
            <p>If you are currently on a ZilaCab ride and face any issue — safety concern, route problem, or driver misconduct — call us immediately. This line is always staffed.</p>
            <div className="em-number">1800-XXX-XXXX</div>
            <div className="em-sub">Toll-free · Always answered · Any network</div>
            <div className="em-actions">
              <a href="tel:1800XXXXXXX" className="btn-or">📞 Call now</a>
              <a href="https://wa.me/919800000000" target="_blank" rel="noopener noreferrer" className="btn-ghost">💬 WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
